<template>
  <div class="w-full h-full overflow-auto">
    <div
      v-if="!isLoading && chartData && chartOptions"
      ref="chartContainer"
      class="w-full min-h-full p-4"
    >
      <div v-if="!isPreview" class="flex justify-end mb-4">
        <UButton
          icon="i-lucide-search"
          class="cursor-pointer"
          variant="outline"
          color="info"
          @click="navigateTo('/preview')"
        />
      </div>
      <div class="w-full h-full min-h-[400px]">
        <Line
          v-if="!isBarChart"
          :key="`line-${getSnapGaps}`"
          ref="chartRef"
          :data="chartData"
          :options="chartOptions"
        />
        <Bar v-else ref="chartRef" :data="chartData" :options="chartOptions" />
      </div>
    </div>
    <div v-else class="w-full h-full">
      <div class="p-6 flex items-center justify-center w-full h-full">
        <UProgress v-if="isLoading" animation="swing" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { Line, Bar } from "vue-chartjs";
import type { ChartOptions } from "chart.js";
import { useRoute } from "vue-router";
import { useGetApiData } from "../composables/useGetApiData";
import { useApp } from "../composables/useApp";
import { annotationData } from "../utils/annotation-lintes";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";

const chartRef = ref<{ chart: { canvas: HTMLCanvasElement } } | undefined>(undefined);

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin,
);

interface CustomChartData {
  labels: string[];
  datasets: Array<{
    label: string;
    data: (number | null)[];
    borderColor?: string;
    backgroundColor?: string | string[];
    fill?: boolean;
    tension?: number;
    pointRadius?: number;
    borderWidth?: number;
    spanGaps?: boolean;
  }>;
}

const route = useRoute();

const isBarChart = computed(() => {
  return route.query.chartType === "bar";
});

const getSnapGaps = computed(() => {
  return route.query.showThresholds === "true";
});

const { isPreview, navigateTo } = useApp();

const { chartData: apiChartData, querySpecies, isLoading } = useGetApiData();

const chartData = ref<CustomChartData | undefined>(undefined);
const chartOptions = ref<ChartOptions<"line" | "bar"> | undefined>(undefined);

watch(apiChartData, (_apiChartData) => {
  if (!_apiChartData) {
    chartData.value = undefined;
    return;
  }

  updateChartData();
});

// Watch for route changes to update chart display options
watch(
  () => [route.query.chartType, route.query.showThresholds],
  () => {
    if (chartData.value) {
      updateChartData();
    }
  },
);

const updateChartData = () => {
  if (!apiChartData.value) return;

  const chartDataArray = Object.values(apiChartData.value)[0];
  if (!Array.isArray(chartDataArray)) {
    console.error("Invalid chart data format");
    return;
  }

  const extractedChartData = chartDataArray.map((item: any) => {
    return {
      Result: item.IPM25?.airQualityObserved || item.IPM25?.airQualityIndex,
      DateTime: item.dateObservedFrom,
    };
  });

  const labels = extractedChartData.map((item: any, idx: number, arr: any[]) => {
    const date = new Date(item.DateTime);
    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" });
    const label = `${day} ${month}`;

    if (
      idx === 0 ||
      label !==
        (() => {
          const prevItem = arr[idx - 1];
          if (!prevItem) return "";
          const prevDate = new Date(prevItem.DateTime);
          return `${prevDate.getDate()} ${prevDate.toLocaleString("en-US", { month: "short" })}`;
        })()
    ) {
      return label;
    }
    return "";
  });

  const data = extractedChartData.map((item: any) => {
    return item.Result === null || item.Result === undefined ? null : Number(item.Result);
  });

  const allAnnotations = annotationData((querySpecies.value as string) || "");

  // Filter annotations based on requirements
  const filteredAnnotations: Record<string, AnnotationItem> = {};

  // Get the data values to check which lines should be shown
  const dataValues = data.filter((val: number | null) => val !== null && val !== undefined);

  Object.entries(allAnnotations).forEach(([key, annotation]: [string, AnnotationItem]) => {
    const yValue = annotation.yMin; // All annotations have same yMin and yMax

    // Always show mod1 (first moderate line)
    if (key === "mod1") {
      filteredAnnotations[key] = annotation;
      return;
    }

    // Include all green lines (low2, low3) regardless of data
    if (key === "low2" || key === "low3") {
      filteredAnnotations[key] = annotation;
      return;
    }

    // Only show other lines if they have data points that match or exceed their value
    if (dataValues.some((val: number | null) => val !== null && val >= yValue)) {
      filteredAnnotations[key] = annotation;
    }
  });

  const annotation = filteredAnnotations;

  // Configure dataset based on chart type
  const dataset = isBarChart.value
    ? {
        label: "LABEL",
        data,
        backgroundColor: "rgba(54, 162, 235, 0.6)",
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 1,
      }
    : {
        label: "LABEL",
        data,
        borderColor: "rgba(54, 162, 235, 1)",
        backgroundColor: "rgba(54, 162, 235, 0.6)",
        fill: false,
        tension: 0.2,
        pointRadius: 0,
        borderWidth: 1.5,
        spanGaps: getSnapGaps.value,
      };

  chartData.value = {
    labels,
    datasets: [dataset],
  };

  chartOptions.value = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: "top" },
      title: { display: true, text: "LABEL" },
      annotation: {
        annotations: annotation as any,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { display: false },
        ticks: { color: "#000" },
      },
      x: {
        grid: { display: false },
        ticks: {
          color: "#000",
          maxRotation: 0,
          minRotation: 0,
          autoSkip: false,
          maxTicksLimit: undefined,
        },
      },
    },
  };
};
</script>
